import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { fetchShopJson, SHOP_IMAGE_BASE } from "./shopApi";

function getInitials(firstname, lastname) {
  const first = (firstname || "").trim().charAt(0);
  const last = (lastname || "").trim().charAt(0);
  return `${first}${last}`.trim().toUpperCase() || "AE";
}

function getVisibleColumns(width) {
  if (width <= 640) return 2;
  if (width <= 980) return 3;
  if (width <= 1280) return 4;
  return 5;
}

export default function AccountExecutiveSection() {
  const navigate = useNavigate();
  const [accountExecutives, setAccountExecutives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [visibleColumns, setVisibleColumns] = useState(() => getVisibleColumns(window.innerWidth || 1440));

  useEffect(() => {
    fetchShopJson("/get_account_executives.php")
      .then((data) => setAccountExecutives(Array.isArray(data) ? data : []))
      .catch((error) => console.error("Failed to load account executives", error))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setVisibleColumns(getVisibleColumns(window.innerWidth || 1440));
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const filteredExecutives = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) {
      return accountExecutives;
    }

    return accountExecutives.filter((executive) => {
      const name = `${executive.firstname || ""} ${executive.lastname || ""}`.toLowerCase();
      return name.includes(term);
    });
  }, [accountExecutives, search]);

  useEffect(() => {
    setCurrentPage(0);
  }, [search, visibleColumns]);

  const totalPages = Math.max(1, Math.ceil(filteredExecutives.length / visibleColumns));
  const safePage = Math.min(currentPage, totalPages - 1);
  const visibleExecutives = filteredExecutives.slice(
    safePage * visibleColumns,
    safePage * visibleColumns + visibleColumns
  );

  if (loading || !accountExecutives.length) {
    return null;
  }

  return (
    <section className="account-executive-section">
      <div className="category-section__copy account-executive-section__copy account-executive-section__copy--centered">
        <p className="category-section__eyebrow">Account Executive</p>
        <h2>Meet the account executives behind the collection</h2>
        <p>
          Browse trusted account executives, see their available pieces, and open their own curated seller page.
        </p>
      </div>

      <div className="account-executive-section__tools account-executive-section__tools--under">
        <label className="account-executive-search">
          <span>Search account executive</span>
          <input
            type="text"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Type a name"
          />
        </label>
      </div>

      <div className="account-executive-toolbar">
        <p>
          Showing <strong>{visibleExecutives.length}</strong> of <strong>{filteredExecutives.length}</strong> account executive
          {filteredExecutives.length === 1 ? "" : "s"}
        </p>
        <p className="account-executive-toolbar__page">
          Page {safePage + 1} of {totalPages}
        </p>
      </div>

      <div className="account-executive-carousel">
        <button
          type="button"
          className="account-executive-nav__button account-executive-nav__button--edge"
          onClick={() => setCurrentPage((page) => Math.max(0, page - 1))}
          disabled={safePage === 0}
        >
          Back
        </button>

        <div
          className="account-executive-grid account-executive-grid--compact"
          style={{ gridTemplateColumns: `repeat(${Math.max(1, visibleColumns)}, minmax(0, 1fr))` }}
        >
          {visibleExecutives.map((executive) => (
            <button
              key={executive.id}
              type="button"
              className="account-executive-card account-executive-card--compact"
              onClick={() => navigate(`/account-executive/${executive.id}`)}
            >
              <div className="account-executive-card__avatar">
                {executive.photo ? (
                  <img
                    src={`${SHOP_IMAGE_BASE}${executive.photo}`}
                    alt={`${executive.firstname} ${executive.lastname}`}
                  />
                ) : (
                  <span>{getInitials(executive.firstname, executive.lastname)}</span>
                )}
                <span className="account-executive-card__hover">Check their collection</span>
                <span className="account-executive-card__badge">
                  <span className="account-executive-card__badge-icon">{"\u2713"}</span>
                  Verified
                </span>
              </div>
              <div className="account-executive-card__body">
                <h3>{`${executive.firstname || ""} ${executive.lastname || ""}`.trim()}</h3>
                <p>{executive.designation || "Account Executive"}</p>
                <div className="account-executive-card__stats">
                  <span>{executive.available_items || 0} available</span>
                  <span>{executive.total_items || 0} uploaded</span>
                </div>
              </div>
              <div className="account-executive-card__meta">
                <span className="account-executive-card__trust">Legit seller</span>
              </div>
            </button>
          ))}
        </div>

        <button
          type="button"
          className="account-executive-nav__button account-executive-nav__button--edge"
          onClick={() => setCurrentPage((page) => Math.min(totalPages - 1, page + 1))}
          disabled={safePage >= totalPages - 1}
        >
          Next
        </button>
      </div>

      {!visibleExecutives.length ? (
        <div className="catalog-empty-state">
          No account executive matched that name yet.
        </div>
      ) : null}
    </section>
  );
}
